import { Anomaly, AnomalyApi, RequestPutAnomaly } from '@api/twin/AnomalyApi';
import { useTeiaViewerContext } from '@stereograph/teiaviewer';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usePluginTranslation } from '@translation';
import { anomalyQueryKeys } from '../query-keys';

let NOTIFICATION_PUT_ANOMALY = '';

export const usePutAnomaly = (projectId: number) => {
  const { t } = usePluginTranslation();
  const { viewer } = useTeiaViewerContext();
  const client = viewer.twinApiClient;
  const queryClient = useQueryClient();

  const { mutate: putAnomaly, isPending } = useMutation<Anomaly, Error, RequestPutAnomaly>({
    mutationFn: async (anomaly) => {
      return await AnomalyApi(client).putAnomaly(projectId, anomaly);
    },
    onMutate: (variable) => {
      NOTIFICATION_PUT_ANOMALY = `notification_put_anomaly_${variable.id}`;
      viewer.notificationCenter.pushNotification({
        type: t('anomaly_plugin.notification_type_text'),
        status: 'pending',
        message: t('anomaly_plugin.widget.form.submit.update.loading.text'),
        title: t('anomaly_plugin.widget.form.submit.update.notification_title'),
        displayed: true,
      }, NOTIFICATION_PUT_ANOMALY);
    },
    onSuccess: async (_response, variable) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: anomalyQueryKeys.anomaly(projectId, variable.id),
        }),
        queryClient.invalidateQueries({
          queryKey: anomalyQueryKeys.anomalies_default(projectId),
        }),
        queryClient.invalidateQueries({
          queryKey: anomalyQueryKeys.history_default(variable.id),
        }),
      ]);

      viewer.notificationCenter.pushNotification({
        type: t('anomaly_plugin.notification_type_text'),
        status: 'success',
        message: t('anomaly_plugin.widget.form.submit.update.success.text', {
          title: variable.title,
        }),
        title: t('anomaly_plugin.widget.form.submit.update.notification_title'),
        displayed: true,
      }, NOTIFICATION_PUT_ANOMALY);
    },
    onError: (_response, variable) => {
      viewer.notificationCenter.pushNotification({
        type: t('anomaly_plugin.notification_type_text'),
        status: 'error',
        message: t('anomaly_plugin.widget.form.submit.update.error.text', {
          title: variable.title,
        }),
        title: t('anomaly_plugin.widget.form.submit.update.notification_title'),
        displayed: true,
      }, NOTIFICATION_PUT_ANOMALY);
    },
  });

  return {
    putAnomaly,
    isPending,
  };
};
